import Skeleton from '@mui/material/Skeleton'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useLazyGetVacationHoldListQuery } from 'api/accounts/vacationHoldsCrud'
import VacationHoldChip from 'components/common/chip/VacationHoldChip'
import VacationHoldDialog from 'components/customers/accounts/vacation-holds/VacationHoldDialog'
import { HOLD_TYPES } from 'settings/constants/service'
import { createDateFromString, formatDateToFEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { isVacationHoldActiveWithinRange } from 'utils/vacation'

import type { HoldsRootState } from './types'

const { HOLD, VACATION } = HOLD_TYPES

const formatDate = (date: string | null) => (date ? formatDateToFEFormatDateFns(createDateFromString(date)) : null)

const VacationHoldChipAccountHeader: React.FC = () => {
  const accountId = useSelector((state: HoldsRootState) => state.CustomersReducer?.customerDetails?.id)
  const [getVacationHoldList, { data, isLoading, isFetching, isSuccess }] = useLazyGetVacationHoldListQuery()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const isInitialLoading = (isLoading || isFetching) && !data

  const activeHolds = useMemo(
    () =>
      (data?.vacationHolds ?? []).filter(
        ({ active, startDate, endDate }) => active && startDate && isVacationHoldActiveWithinRange(startDate, endDate)
      ),
    [data]
  )
  const chipType = activeHolds.every(({ type }) => type === HOLD) ? HOLD : VACATION
  const [firstHold] = activeHolds
  const chipLabel =
    activeHolds.length > 1
      ? `${activeHolds.length} holds`
      : [formatDate(firstHold?.startDate ?? null), formatDate(firstHold?.endDate ?? null)].join('-')

  const handleOpenDialog = useCallback(() => setIsDialogOpen(true), [])
  const handleCloseDialog = useCallback(() => setIsDialogOpen(false), [])

  useEffect(() => {
    if (!accountId) return
    getVacationHoldList({ accountId, deleted: false }).unwrap().catch(handleError)
  }, [accountId, getVacationHoldList])

  if (!accountId) return null

  return (
    <>
      {isInitialLoading && (
        <Skeleton>
          <VacationHoldChip label="" />
        </Skeleton>
      )}
      {isSuccess && activeHolds.length > 0 && (
        <VacationHoldChip clickable isOpen={isDialogOpen} label={chipLabel} type={chipType} onClick={handleOpenDialog} />
      )}
      <VacationHoldDialog open={isDialogOpen} row={{ accountId }} onClose={handleCloseDialog} />
    </>
  )
}

export default VacationHoldChipAccountHeader
